import { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import Acquirer from "enums/Acquirer";
import {
  PaymentLinkAmountMode,
  PaymentLinkAvailability,
  PaymentLinkSuccessType,
  PaymentLinkType,
} from "enums/PaymentLink";

const payLinkClient = Axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const VIEW_RECORDED_PREFIX = "payLinkViewed:";

export interface PayLinkQuote {
  intendedAmount: number;
  discountAmount: number;
  feeCoverAmount: number;
  tipAmount: number;
  totalToCharge: number;
  totalWithFeeCover?: number;
}

export interface PublicPaymentLink {
  slug: string;
  name: string;
  description?: string;
  amountMode: PaymentLinkAmountMode;
  amount?: number;
  minAmount?: number;
  maxAmount?: number;
  presetAmounts?: number[];
  currency: string;
  linkType: PaymentLinkType;
  interval?: string;
  maxPayments?: number;
  endsAt?: number;
  tip?: {
    enabled: boolean;
    presetPercentages?: number[];
  };
  successBehavior?: {
    type: PaymentLinkSuccessType;
    message?: string;
    redirectUrl?: string;
  };
}

export interface PublicCompanyDetails {
  companyName?: string;
  companyPhone?: string;
  companyWebsite?: string;
  companyLogoUrl?: string;
  companyEmail?: string;
}

export interface PayerIdentity {
  verificationRequired: boolean;
  payerEmail?: string;
  payerName?: string;
}

export interface PayLinkAttachment {
  id: string;
  assetUrl: string;
  extension?: string;
  filename?: string;
}

export interface PayLinkPageData {
  availability: PaymentLinkAvailability;
  inactiveMessage?: string;
  paymentLink?: PublicPaymentLink;
  companyDetails?: PublicCompanyDetails;
  acquirer?: {
    acquirer: Acquirer;
    publishableKey: string;
    componentType: string;
  };
  payerIdentity?: PayerIdentity;
  attachments?: PayLinkAttachment[];
  feeCoverEnabled?: boolean;
  cashDiscountEnabled?: boolean;
  allowedPaymentMethods?: string[];
  quotes?: Record<string, PayLinkQuote> | null;
}

export interface PayLinkSession {
  feeCoverAccepted?: boolean;
  paymentLinkPaymentId?: string;
  subscriptionId?: string;
  subscriptionChargeId?: string;
  manageToken?: string;
  clientSecret: string;
  customerSessionClientSecret?: string;
  paymentMethodType: string;
  allowedPaymentMethods: string[];
  quote: PayLinkQuote;
}

export interface CreateSessionArgs {
  payerEmail: string;
  amount?: number | null;
  feeCoverAccepted?: boolean;
  tipAmount?: number;
  paymentMethodType?: string;
  clientReferenceId?: string | null;
  mandateAccepted?: boolean;
  mandateTextVersion?: string;
  verificationCredential?: string | null;
}

export function usePayLinkSession(
  slug?: string,
  contactToken: string | null = null,
) {
  const requestVersion = useRef(0);
  // Each amount supersedes the last, so a slow answer never prices a newer one.
  const quoteVersion = useRef(0);
  // The link the current quotes price, so reloading that link can keep them.
  const pricedSlug = useRef<string | undefined>(undefined);
  const [page, setPage] = useState<PayLinkPageData | null>(null);
  const [session, setSession] = useState<PayLinkSession | null>(null);
  const [quotes, setQuotes] = useState<Record<string, PayLinkQuote> | null>(
    null,
  );
  // True while the amount on screen is being priced, so nothing is chosen on the last one's prices.
  const [quoting, setQuoting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationRequired, setVerificationRequired] = useState(false);

  useEffect(() => {
    requestVersion.current += 1;
    setSession(null);
    return () => {
      requestVersion.current += 1;
    };
  }, [slug, contactToken]);

  // Another link's pricing, or a quote still in flight for it, never prices this one.
  useEffect(() => {
    quoteVersion.current += 1;
    pricedSlug.current = undefined;
    setQuotes(null);
    setQuoting(false);
  }, [slug]);

  /* Redirects do not prove payment success; fetch the actual outcome. */
  const loadPaymentStatus = useCallback(
    async (acquirerPaymentId: string): Promise<string | null> => {
      if (!slug || !acquirerPaymentId) return null;

      try {
        const { data } = await payLinkClient.get<{ status: string }>(
          `/pay-link/${slug}/payments/${acquirerPaymentId}`,
        );

        return data.status || null;
      } catch {
        return null;
      }
    },
    [slug],
  );

  const loadPage = useCallback(async () => {
    if (!slug) return;

    const version = requestVersion.current;
    setLoading(true);
    try {
      const { data } = await payLinkClient.get<PayLinkPageData>(
        `/pay-link/${slug}`,
        {
          headers: contactToken
            ? { Authorization: `Bearer ${contactToken}` }
            : {},
        },
      );
      if (version !== requestVersion.current) return;
      // Signing in or out reloads without the chosen amount's pricing, which does not depend on the payer.
      const keepPricing = !data.quotes && pricedSlug.current === slug;
      setPage((current) =>
        keepPricing
          ? {
              ...data,
              allowedPaymentMethods:
                data.allowedPaymentMethods ?? current?.allowedPaymentMethods,
            }
          : data,
      );
      if (!keepPricing) {
        setQuotes(data.quotes || null);
        pricedSlug.current = slug;
      }
      setError(null);
    } catch (requestError) {
      setError(messageOf(requestError) || "This payment link is unavailable");
    } finally {
      setLoading(false);
    }
  }, [slug, contactToken]);

  /* Record before awaiting to prevent duplicate views from StrictMode or redirects. */
  const recordView = useCallback(async () => {
    if (!slug) return;

    try {
      const key = `${VIEW_RECORDED_PREFIX}${slug}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // Storage can be unavailable in private browsing.
    }

    await payLinkClient.post(`/pay-link/${slug}/view`).catch(() => undefined);
  }, [slug]);

  const requestQuote = useCallback(
    async (amount: number | null) => {
      const version = ++quoteVersion.current;
      if (!slug) return;

      // An amount without a price offers no methods, so nothing from the last one may stay.
      const clearPricing = () => {
        setQuotes(null);
        setPage((current) =>
          current ? { ...current, allowedPaymentMethods: [] } : current,
        );
      };

      if (amount == null) {
        clearPricing();
        setQuoting(false);
        setError(null);
        return;
      }

      setQuoting(true);
      try {
        const { data } = await payLinkClient.post<PayLinkPageData>(
          `/pay-link/${slug}/quotes`,
          { amount },
        );
        if (version !== quoteVersion.current) return;
        setQuotes(data.quotes || null);
        pricedSlug.current = slug;
        // Payer-chosen links receive their available methods with the first amount quote.
        setPage((current) =>
          current
            ? {
                ...current,
                allowedPaymentMethods: data.allowedPaymentMethods || [],
              }
            : current,
        );
        setError(null);
      } catch (requestError) {
        if (version !== quoteVersion.current) return;
        clearPricing();
        setError(messageOf(requestError) || "Could not price that amount");
      } finally {
        if (version === quoteVersion.current) setQuoting(false);
      }
    },
    [slug],
  );

  const startSession = useCallback(
    async (args: CreateSessionArgs) => {
      if (!slug) return null;

      const version = requestVersion.current;
      setLoading(true);
      try {
        const { data } = await payLinkClient.post<PayLinkSession>(
          `/pay-link/${slug}/sessions`,
          args,
        );
        if (version !== requestVersion.current) return null;
        setSession({
          ...data,
          feeCoverAccepted: args.feeCoverAccepted === true,
        });
        setError(null);
        setVerificationRequired(false);
        return data;
      } catch (requestError) {
        if (version !== requestVersion.current) return null;
        setVerificationRequired(isVerificationRequired(requestError));
        setError(messageOf(requestError) || "Could not start this payment");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [slug],
  );

  /* Reprice the single-method intent before confirming a changed method or fee cover. */
  const repriceSession = useCallback(
    async (changes: {
      paymentMethodType?: string;
      feeCoverAccepted?: boolean;
      tipAmount?: number;
    }) => {
      // Subscription amounts are frozen by the mandate.
      if (!slug || !session?.paymentLinkPaymentId) return false;

      // Null when a logout or a link change dropped the session mid-request: never restore it.
      const version = requestVersion.current;
      try {
        const { data } = await payLinkClient.patch<{ quote: PayLinkQuote }>(
          `/pay-link/${slug}/sessions/${session.paymentLinkPaymentId}`,
          {
            paymentMethodType:
              changes.paymentMethodType || session.paymentMethodType,
            feeCoverAccepted:
              changes.feeCoverAccepted ?? session.feeCoverAccepted,
            tipAmount: changes.tipAmount ?? session.quote.tipAmount,
          },
        );
        if (version !== requestVersion.current) return null;

        setSession({
          ...session,
          feeCoverAccepted:
            changes.feeCoverAccepted ?? session.feeCoverAccepted,
          paymentMethodType:
            changes.paymentMethodType || session.paymentMethodType,
          quote: data.quote,
        });
        setError(null);

        return true;
      } catch (requestError) {
        if (version !== requestVersion.current) return null;
        setError(messageOf(requestError) || "Could not update this payment");

        return false;
      }
    },
    [slug, session],
  );

  return {
    page,
    session,
    quotes,
    quoting,
    loading,
    error,
    verificationRequired,
    setError,
    loadPage,
    loadPaymentStatus,
    recordView,
    requestQuote,
    startSession,
    repriceSession,
  };
}

function isVerificationRequired(error: unknown): boolean {
  return (
    (error as { response?: { data?: { verificationRequired?: boolean } } })
      ?.response?.data?.verificationRequired === true
  );
}

function messageOf(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } })?.response?.data
    ?.error;
}
